import type { IServiceEmployee, ISuperuser, IUser } from "@/services/types";

export interface NameFallback {
  fullName?: string;
  firstName?: string;
  lastName?: string;
}

const trimText = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const combineParts = (...parts: (string | undefined)[]): string | undefined => {
  const cleaned = parts
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));
  return cleaned.length ? cleaned.join(" ") : undefined;
};

const preferValue = (
  current?: string,
  fallback?: string,
): string | undefined => (current?.trim() ? current : fallback?.trim());

const mergeFallbacks = (
  existing: NameFallback | undefined,
  next: NameFallback | undefined,
): NameFallback => ({
  fullName: preferValue(existing?.fullName, next?.fullName),
  firstName: preferValue(existing?.firstName, next?.firstName),
  lastName: preferValue(existing?.lastName, next?.lastName),
});

export const normalizeEmail = (email?: string): string | null => {
  const trimmed = email?.trim().toLowerCase();
  return trimmed || null;
};

/**
 * Parse a display name from an email address as fallback.
 * Handles common patterns like "First.Last@domain.com" or "first_last@domain.com"
 *
 * @example
 * parseNameFromEmail("John.Doe@example.com") // "John Doe"
 * parseNameFromEmail("jane_smith@example.com") // "Jane Smith"
 * parseNameFromEmail("bob-jones@example.com") // "Bob Jones"
 */
export const parseNameFromEmail = (email?: string): string | null => {
  if (!email) return null;

  const localPart = email.split("@")[0];
  if (!localPart) return null;

  // Split by common separators (dot, underscore, hyphen)
  const parts = localPart.split(/[._-]/);

  // Capitalize each part
  const capitalized = parts.map(
    (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
  );

  return capitalized.join(" ");
};

export const upsertNameFallback = (
  storage: Map<string, NameFallback>,
  email: string | undefined,
  fallback: NameFallback | undefined,
) => {
  if (!fallback) return;
  const normalized = normalizeEmail(email);
  if (!normalized) return;
  const existing = storage.get(normalized);
  storage.set(normalized, mergeFallbacks(existing, fallback));
};

export const fallbackFromEmployee = (
  employee: IServiceEmployee,
): NameFallback => ({
  fullName:
    trimText(employee.name) ??
    combineParts(employee.given_name, employee.surname),
  firstName: trimText(employee.given_name),
  lastName: trimText(employee.surname),
});

export const fallbackFromUser = (user: IUser): NameFallback => ({
  fullName:
    trimText(user.name) ?? combineParts(user.first_name, user.last_name),
  firstName: trimText(user.first_name),
  lastName: trimText(user.last_name),
});

export const fallbackFromSuperuser = (superuser: ISuperuser): NameFallback => {
  const fullName = trimText(superuser.name);
  const [firstPart, ...rest] = fullName?.split(" ") ?? [];
  const lastPart = rest.join(" ").trim();
  return {
    fullName,
    firstName: trimText(firstPart),
    lastName: lastPart ? lastPart : undefined,
  };
};

export const applyNameFallback = <
  T extends {
    name?: string;
    first_name?: string;
    last_name?: string;
  },
>(
  entity: T,
  fallback?: NameFallback,
): T => {
  if (!fallback) {
    return entity;
  }

  const needsName = !entity.name?.trim();
  const needsFirst = !entity.first_name?.trim();
  const needsLast = !entity.last_name?.trim();

  if (!needsName && !needsFirst && !needsLast) {
    return entity;
  }

  return {
    ...entity,
    ...(needsName && fallback.fullName
      ? { name: fallback.fullName }
      : undefined),
    ...(needsFirst && fallback.firstName
      ? { first_name: fallback.firstName }
      : undefined),
    ...(needsLast && fallback.lastName
      ? { last_name: fallback.lastName }
      : undefined),
  };
};

/** Minimal interface for employee name formatting */
export interface EmployeeNameFields {
  name?: string;
  given_name?: string;
  surname?: string;
  email?: string;
}

export const formatEmployeeName = (
  employee: EmployeeNameFields,
  fallbackEmail?: string,
): string => {
  return (
    trimText(employee.name) ??
    combineParts(employee.given_name, employee.surname) ??
    trimText(fallbackEmail ?? employee.email) ??
    "Ukjent ansatt"
  );
};

/**
 * Format a user's display name with fallbacks.
 * Priority: name > first_name + last_name > email > fallback
 */
export const formatUserName = (
  user: IUser,
  fallbackValue = "Ukjent bruker",
): string => {
  return (
    trimText(user.name) ??
    combineParts(user.first_name, user.last_name) ??
    trimText(user.email) ??
    fallbackValue
  );
};

/**
 * Derived name fields for a user-like entity.
 * All fields are prepped (trimmed) with intelligent fallbacks.
 */
export interface DerivedUserNames {
  /** First name (trimmed), empty string if not available */
  firstName: string;
  /** Last name (trimmed), empty string if not available */
  lastName: string;
  /** Full name from first + last, empty string if neither available */
  fullName: string;
  /** Name field (trimmed), undefined if not available */
  name: string | undefined;
  /** Parsed name from email (e.g., "John.Doe@x.com" → "John Doe") */
  parsedFromEmail: string | null;
  /** Best display name with fallback chain: fullName → name → parsedFromEmail → email */
  displayName: string;
}

export interface UserLikeEntity {
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
}

/**
 * Derive all name-related fields from a user-like entity.
 * Returns prepped values with intelligent fallbacks for flexible use.
 *
 * @example
 * const { displayName, firstName } = deriveUserNames(user);
 * // Or destructure just what you need:
 * const { displayName } = deriveUserNames(superuser);
 */
export const deriveUserNames = (user: UserLikeEntity): DerivedUserNames => {
  const firstName = user.first_name?.trim() ?? "";
  const lastName = user.last_name?.trim() ?? "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const name = user.name?.trim() || undefined;
  const parsedFromEmail = parseNameFromEmail(user.email);
  const displayName = fullName || name || parsedFromEmail || user.email;

  return {
    firstName,
    lastName,
    fullName,
    name,
    parsedFromEmail,
    displayName,
  };
};
